import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import cx from 'classnames';
import { Breadcrumbs as BreadCrumbsComponent } from '@material-tailwind/react';
import { AiOutlineHome } from 'react-icons/ai';
import AppButton from '@/components/atoms/AppButton';
import { Text } from '@/components/atoms/AppText';
import { BREADCRUMB_PROPS } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { LAYOUT_TEST_IDS as TID } from '@/constant/testIds/layout.testIds';
import { BreadCrumbRoute } from './DefaultNavItem';

/* ================= MATCH ROUTE ================= */
const matchRoute = (routePath: string, currentPath: string) => {
  const r = routePath.split('/');
  const c = currentPath.split('/');

  if (r.length !== c.length) return false;

  return r.every((p, i) => p.startsWith(':') || p === c[i]);
};

/* ================= ICON ================= */
const getIcon = (path: string) => {
  const route = BreadCrumbRoute.find((r) => matchRoute(r.path, path));
  return route?.icon;
};

/* ================= SMART PATH ================= */
const buildPath = (route: string, params: any) => {
  let finalPath = route;

  Object.keys(params || {}).forEach((key) => {
    finalPath = finalPath.replace(`:${key}`, params[key]);
  });

  return finalPath;
};

export default function Breadcrumbs() {
  const breadcrumbs = useBreadcrumbs(BreadCrumbRoute);
  const navigate = useNavigate();
  const params = useParams();

  return (
    <div data-testid={tid(TID.BREADCRUMB)}>
      <BreadCrumbsComponent
        className="flex items-center gap-1 bg-transparent p-0 m-0 shadow-none"
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
      >
        {/* ================= BRAND ================= */}
        {/* <Text as="span" size="xl" weight="semibold" color="soft" tracking="wide" className="px-2">
        Gateway Portal
      </Text> */}

        {breadcrumbs.map(({ match, breadcrumb }, index) => {
          const isLast = index === breadcrumbs.length - 1;

          const Icon = getIcon(match.pathname);

          const className = cx(
            `
          flex items-center gap-1
          text-sm font-medium
          transition-colors duration-200
          `,
            {
              'text-gray-900 dark:text-gray-100': isLast,
              'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white':
                !isLast,
            },
          );

          /* ================= HOME ================= */
          if (
            React.isValidElement(breadcrumb) &&
            breadcrumb.props?.children === BREADCRUMB_PROPS.HOME_LABEL
          ) {
            return (
              <AppButton
                key={index}
                data-testid={tid(TID.BREADCRUMB_HOME_BTN)}
                variant="ghost"
                shape="square"
                size="sm"
                icon={<AiOutlineHome size={16} />}
                onClick={() => navigate(BREADCRUMB_PROPS.HOME_NAVIGATE_PATH)}
              />
            );
          }

          /* ================= FIXED NAVIGATION ================= */
          const targetPath = BreadCrumbRoute.find((r) =>
            matchRoute(r.path, match.pathname),
          )?.path;

          const safePath = targetPath
            ? buildPath(targetPath, params)
            : match.pathname;

          return isLast ? (
            <span key={index} className={className}>
              {Icon && (
                <Icon size={14} className="text-gray-700 dark:text-gray-300" />
              )}
              {breadcrumb}
            </span>
          ) : (
            <Link
              key={index}
              to={safePath}
              data-testid={tid(TID.BREADCRUMB_LINK(
                typeof breadcrumb === 'string'
                  ? breadcrumb
                  : String((breadcrumb as any)?.props?.children ?? index),
              ))}
              className={className}
            >
              {Icon && (
                <Icon size={14} className="text-gray-500 dark:text-gray-400" />
              )}
              {breadcrumb}
            </Link>
          );
        })}
      </BreadCrumbsComponent>
    </div>
  );
}
